import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { agendaSemanal } from "../../lib/adminApi";
import { Card, EstadoBadge, MarcacaoLink, PageTitle, ClinicaLabel } from "../ui";

const HORAS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const DIAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function mondayOf(d) {
  const x = new Date(d);
  const day = x.getDay() || 7; // 0=Dom→7
  if (day !== 1) x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
}

function isoLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function AdminAgendaPage() {
  const [base, setBase] = useState(() => mondayOf(new Date()));
  const [clinica, setClinica] = useState("");
  const [data, setData] = useState({ marcacoes: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    agendaSemanal(isoLocal(base), clinica || undefined)
      .then(setData).finally(() => setLoading(false));
  }, [base, clinica]);

  const dias = useMemo(() => Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(base); d.setDate(d.getDate() + i); return d;
  }), [base]);

  // Group marcacoes by day+hour
  const grouped = useMemo(() => {
    const map = {};
    data.marcacoes.forEach((m) => {
      const dt = new Date(m.data_hora);
      const key = `${isoLocal(dt)}|${dt.getHours()}`;
      (map[key] = map[key] || []).push(m);
    });
    return map;
  }, [data]);

  return (
    <div data-testid="admin-agenda">
      <PageTitle subtitle="Vista semanal das marcações (read-only).">Agenda</PageTitle>

      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => { const x = new Date(base); x.setDate(x.getDate() - 7); setBase(mondayOf(x)); }} data-testid="prev-week" className="h-9 w-9 grid place-items-center rounded-lg bg-white border border-[#1E5F6E]/15"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setBase(mondayOf(new Date()))} data-testid="this-week" className="rounded-lg bg-white border border-[#1E5F6E]/15 px-3 py-2 text-xs font-bold">Esta semana</button>
            <button onClick={() => { const x = new Date(base); x.setDate(x.getDate() + 7); setBase(mondayOf(x)); }} data-testid="next-week" className="h-9 w-9 grid place-items-center rounded-lg bg-white border border-[#1E5F6E]/15"><ChevronRight className="h-4 w-4" /></button>
            <div className="ml-3 font-display font-bold">{isoLocal(base)} → {isoLocal(dias[6])}</div>
          </div>
          <select value={clinica} onChange={(e) => setClinica(e.target.value)} data-testid="agenda-clinica" className="rounded-lg bg-white border border-[#1E5F6E]/15 px-3 py-2 text-sm">
            <option value="">Todas as clínicas</option>
            <option value="ribeira-brava">Ribeira Brava</option>
            <option value="calheta">Calheta</option>
          </select>
        </div>
      </Card>

      <Card className="!p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#5A5A5A]"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</div>
        ) : (
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
                <th className="text-left p-2 w-16">Hora</th>
                {dias.map((d, i) => (
                  <th key={i} className="text-left p-2">
                    {DIAS[i]} {String(d.getDate()).padStart(2, "0")}/{String(d.getMonth() + 1).padStart(2, "0")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HORAS.map((h) => (
                <tr key={h} className="border-t border-[#1E5F6E]/8">
                  <td className="p-2 font-semibold text-[#5A5A5A] align-top">{String(h).padStart(2, "0")}:00</td>
                  {dias.map((d, i) => {
                    const key = `${isoLocal(d)}|${h}`;
                    const items = grouped[key] || [];
                    return (
                      <td key={i} className="p-2 align-top min-h-[60px]" data-testid={`cell-${i}-${h}`}>
                        <div className="flex flex-col gap-1">
                          {items.map((m) => (
                            <Link
                              key={m.id}
                              to={`/admin/marcacoes/${m.id}`}
                              data-testid={`agenda-item-${m.id}`}
                              className="rounded-lg border px-2 py-1.5 text-[11px] hover:shadow-soft transition-shadow bg-white"
                              style={{ borderLeftWidth: 4, borderLeftColor: estadoCor(m.estado) }}
                            >
                              <div className="font-bold">{new Date(m.data_hora).getHours().toString().padStart(2,"0")}:{new Date(m.data_hora).getMinutes().toString().padStart(2,"0")} · {m.animal.nome}</div>
                              <div className="text-[10px] text-[#5A5A5A] truncate">{m.tutor.nome} · <ClinicaLabel slug={m.clinica_slug} /></div>
                            </Link>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function estadoCor(e) {
  return {
    pendente: "#F59E0B",
    confirmada: "#0EA5E9",
    concluida: "#10B981",
    cancelada: "#A1A1AA",
    no_show: "#EF4444",
  }[e] || "#A1A1AA";
}
